// ---------------------------------------------------------------------------
// Countdown Timer Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

interface CountdownTimerProps {
  deadline: string;
  heading?: string;
  expiredMessage?: string;
  variant?: "inline" | "banner";
}

function isValidIsoDate(value: string): boolean {
  const d = new Date(value);
  return !isNaN(d.getTime());
}

function formatDeadline(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as CountdownTimerProps;

  const deadline = p.deadline ?? "";
  const expiredMessage = p.expiredMessage ?? "This offer has expired.";
  const variant = p.variant ?? "inline";

  const headingHtml = p.heading
    ? `<h2 class="ease-countdown-heading">${text(p.heading)}</h2>\n    `
    : "";

  if (!isValidIsoDate(deadline)) {
    return `<section class="ease-section ease-countdown ease-countdown--${attr(variant)}" aria-label="Countdown timer">
  <div class="ease-countdown-content">
    ${headingHtml}<p class="ease-countdown-expired">${text(expiredMessage)}</p>
  </div>
</section>`;
  }

  const readableDate = formatDeadline(deadline);

  const script = `<script>(function(){var c=document.querySelector('[data-deadline="${attr(deadline)}"]');if(!c)return;var dl=new Date(c.dataset.deadline).getTime();function u(){var n=Date.now(),d=dl-n;if(d<=0){c.querySelector('.ease-countdown-timer').innerHTML='<span class="ease-countdown-expired">${attr(expiredMessage)}</span>';return;}var dd=Math.floor(d/86400000),hh=Math.floor((d%86400000)/3600000),mm=Math.floor((d%3600000)/60000),ss=Math.floor((d%60000)/1000);c.querySelector('.ease-cd-days').textContent=dd;c.querySelector('.ease-cd-hours').textContent=hh;c.querySelector('.ease-cd-mins').textContent=mm;c.querySelector('.ease-cd-secs').textContent=ss;setTimeout(u,1000);}u();})()</script>`;

  return `<section class="ease-section ease-countdown ease-countdown--${attr(variant)}" aria-label="Countdown timer" data-deadline="${attr(deadline)}">
  <div class="ease-countdown-content">
    ${headingHtml}<div class="ease-countdown-timer" aria-live="polite">
      <div class="ease-countdown-unit"><span class="ease-cd-days ease-countdown-value">--</span><span class="ease-countdown-label">Days</span></div>
      <div class="ease-countdown-unit"><span class="ease-cd-hours ease-countdown-value">--</span><span class="ease-countdown-label">Hours</span></div>
      <div class="ease-countdown-unit"><span class="ease-cd-mins ease-countdown-value">--</span><span class="ease-countdown-label">Minutes</span></div>
      <div class="ease-countdown-unit"><span class="ease-cd-secs ease-countdown-value">--</span><span class="ease-countdown-label">Seconds</span></div>
    </div>
    <noscript><p>Deadline: ${text(readableDate)}</p></noscript>
  </div>
</section>
${script}`;
}

registerRenderer("countdown-timer", { render });
